import { ProcessCellForExportParams, ProcessHeaderForExportParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useSolveModeTranslation } from 'hooks/use-solve-mode';
import { useCallback, useRef } from 'react';
import { QuickActions } from 'routes/smart-quote/components/prompt-results/components/quick-actions';
import { QuotesTable } from 'routes/smart-quote/components/prompt-results/components/quote-table';
import { SectionTitle } from 'routes/smart-quote/components/prompt-results/components/section-title';
import {
  SmartQuoteParsingResultWithRowNumber,
  useGroupPromptResultsBySolveMode,
} from 'routes/smart-quote/hooks/use-group-prompt-results';
import { trackSmartQuoteEvent } from 'routes/smart-quote/utils';

type PromptTablesProps = {
  promptId: string;
};

export const PromptTableSections = ({ promptId }: PromptTablesProps) => {
  const tabId = useAssertedActiveTabId();
  const resultsBySolveMode = useGroupPromptResultsBySolveMode(tabId, promptId);
  return (
    <>
      {resultsBySolveMode.map(([solveMode, results]) => (
        <PromptTableSection key={solveMode} solveMode={solveMode} results={results} />
      ))}
    </>
  );
};

const PromptTableSection = ({
  solveMode,
  results,
}: {
  solveMode: string;
  results: SmartQuoteParsingResultWithRowNumber[];
}) => {
  const gridRef = useRef<AgGridReact<SmartQuoteParsingResultWithRowNumber> | null>(null);
  const translateSolveMode = useSolveModeTranslation();
  const translatedSolveMode = translateSolveMode(solveMode);

  const isPlural = results.length > 1;

  const formatColumnHeaderForCsvCopy = useCallback(
    ({ column }: ProcessHeaderForExportParams) => {
      if (column.getColDef().headerName) {
        return column.getColDef().headerName!;
      }
      return translatedSolveMode;
    },
    [translatedSolveMode],
  );

  const formatCellForCsvCopy = useCallback(
    ({ value, node, column }: ProcessCellForExportParams) => {
      if (column.getColId() === 'tenor') {
        return value ? `${value.value} ${value.unit}${value.value > 1 ? 's' : ''}` : '';
      }
      if (column.getColDef().headerComponentParams?.solveMode === solveMode) {
        return node?.data?.solveResult && node.data.isDisplayedPrice
          ? node.data.solveResult + (node.data.solveResultFormat === 'Percent' ? ' %' : '')
          : '';
      }
      return value;
    },
    [solveMode],
  );

  const copyToClipboard = useCallback(async () => {
    const message = gridRef.current?.api.getDataAsCsv({
      allColumns: true,
      columnSeparator: '\t',
      processHeaderCallback: formatColumnHeaderForCsvCopy,
      processCellCallback: formatCellForCsvCopy,
    });

    if (message) {
      trackSmartQuoteEvent('quote actions', 'copy quotes');
      return await navigator.clipboard.writeText(message);
    }
  }, [formatColumnHeaderForCsvCopy, formatCellForCsvCopy]);

  return (
    <>
      <SectionTitle solveMode={solveMode} isPlural={isPlural} className="mb-2" />
      <QuotesTable gridRef={gridRef} solveMode={solveMode} results={results} className="mb-3" />
      <QuickActions results={results} copyToClipboard={copyToClipboard} />
    </>
  );
};
